import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getSocket } from '../../utils/Socket';
import { getSolicitacoesSenhaPendentes, responderSolicitacaoSenha } from '../helpers/Api';

// Painel global de aprovação de senha de exclusão — montado uma vez em
// index.js, igual SocketListener.jsx. Não depende de estar "online" no
// instante exato do pedido: busca pendentes ao montar (cobre quem só entrou
// depois) e também escuta o evento em tempo real (cobre quem já está com a
// tela aberta). Ver decisão #2 em
// docs/ativo/plano-implementacao-senha-exclusao-item-pedido.txt.
const PAPEIS_APROVADORES = ['GERENTE', 'ADMINISTRADOR', 'ADMIN', 'ADM'];

export default function ListenerSolicitacaoSenha() {
  const [pendentes, setPendentes] = useState([]);
  const [respondendo, setRespondendo] = useState({});
  const intervalRef = useRef(null);

  const podeAprovar = PAPEIS_APROVADORES.includes(
    String(sessionStorage.getItem('tipo_usuario') || '').toUpperCase()
  );

  const carregarPendentes = async () => {
    try {
      const res = await getSolicitacoesSenhaPendentes();
      setPendentes(res.data.data || []);
    } catch (err) {
      console.error('Erro ao buscar solicitações de senha pendentes:', err);
    }
  };

  useEffect(() => {
    if (!podeAprovar) return;
    const codOrg = sessionStorage.getItem('cod_org');
    if (!codOrg) return;

    carregarPendentes();
    // Safety net — mesmo padrão de polling do SocketListener.jsx (socket é
    // o mecanismo primário, isso só cobre reconexões perdidas).
    intervalRef.current = setInterval(carregarPendentes, 30000);

    const socket = getSocket();
    const handleNovaSolicitacao = (payload) => {
      toast.info(`🔑 ${payload.nome_solicitante} solicitou autorização: ${payload.label}`);
      carregarPendentes();
    };
    socket.on(`solicita_senha${codOrg}`, handleNovaSolicitacao);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      socket.off(`solicita_senha${codOrg}`, handleNovaSolicitacao);
    };
  }, [podeAprovar]);

  const responder = async (cod_solsenha, aprovar) => {
    setRespondendo((prev) => ({ ...prev, [cod_solsenha]: true }));
    try {
      await responderSolicitacaoSenha(cod_solsenha, { aprovar });
      setPendentes((prev) => prev.filter((p) => p.cod_solsenha !== cod_solsenha));
      toast.success(aprovar ? 'Solicitação aprovada.' : 'Solicitação negada.');
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Erro ao responder solicitação.');
    } finally {
      setRespondendo((prev) => ({ ...prev, [cod_solsenha]: false }));
    }
  };

  if (!podeAprovar || pendentes.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: 1400,
        width: 320,
        maxWidth: '90vw',
      }}
    >
      <div className="card shadow-lg border-0" style={{ borderRadius: 10 }}>
        <div className="card-header bg-dark text-white fw-bold py-2">
          🔑 Solicitações de senha ({pendentes.length})
        </div>
        <div className="card-body p-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {pendentes.map((s) => (
            <div key={s.cod_solsenha} className="border rounded p-2 mb-2">
              <p className="mb-1 small">
                <strong>{s.nome_solicitante}</strong> — {s.label}
                {s.numero_pedidomesa ? ` (Pedido #${s.numero_pedidomesa})` : ''}
              </p>
              {s.descricao_item && <p className="mb-1 small text-muted">{s.descricao_item}</p>}
              {!s.descricao_item && s.obs && <p className="mb-1 small text-muted">{s.obs}</p>}
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-success flex-fill"
                  disabled={respondendo[s.cod_solsenha]}
                  onClick={() => responder(s.cod_solsenha, true)}
                >
                  Aprovar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger flex-fill"
                  disabled={respondendo[s.cod_solsenha]}
                  onClick={() => responder(s.cod_solsenha, false)}
                >
                  Negar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
